/**
 * Rollbar Error Trigger - Production Only
 * Follows compliance rules: production logs only, error-level only, contextual metadata
 * Implements best practices for Shopify storefronts
 */

// Enhanced list of error substrings to ignore - avoiding noise from third-party scripts
const ignoreErrors = [
    'script error',
    'str1.indexOf',
    'unexpected token',
    '_AutofillCallbackHandler',
    '_selectProvince is not defined',
    'provinceSelectorDropdown',
    'proxy',
    'getItem',
    'access denied',
    'push',
    'uninitialized',
    'Unexpected end of script',
    'Unexpected EOF',
    '@context',
    'igEvents',
    'ignoring errors until timeout',
    'load failed',
    'failed to execute',
    'consoleLog',
    'cannot declare a let variable twice',
    'digest',
    'load error',
    'insertbefore',
    'testrollabar',
    'illegal invocation',
    'webpixelsmanager',
    'document._boomrl',
    'symbol.iterator',
    'seek',
    'operation',
    'requestURL.includes',
    'unconfigurable property',
    'solana',
    'utility is not defined',
    'ns_binding_aborted',
    'assign to readonly property',
    'network error',
    'non-error promise rejection',
    'loading css chunk',
    'adnxs',
    'gpt',
    'gtag',
    'facebook',
    'analytics',
    'chat',
    'widget',
    'extension',
    'chrome-extension',
    'moz-extension',
    'resizeobserver loop completed',
    'resizeobserver loop limit exceeded'
];

/**
 * Check if error should be ignored based on compliance rules
 * @param {string} message - Error message
 * @param {string} source - Error source
 * @returns {boolean}
 */
const shouldIgnoreError = (message, source = '') => {
    if (!message) return true;
    
    const messageStr = message.toLowerCase();
    const sourceStr = source.toLowerCase();
    
    // Ignore third-party script errors that aren't from our CDN
    if (source && !sourceStr.includes('/cdn') && !sourceStr.includes(window.location.hostname)) {
        return true;
    }
    
    // Ignore known noise patterns
    return ignoreErrors.some(pattern => messageStr.includes(pattern.toLowerCase()));
};

/**
 * Get comprehensive context for error reporting
 * @returns {object} Context object with storefront-specific metadata
 */
  const getErrorContext = () => {
      const context = {
        date_time: new Date().toLocaleString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        page_referrer: document.referrer,
        error_type: 'auto', // Mark as manually triggered
        viewport: {
            width: window.innerWidth,
            height: window.innerHeight
        },
        connection: navigator.connection ? {
            effective_type: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink
        } : null
    };

    // Add Shopify-specific context if available
    if (window.globalVariables) {
        context.shop_info = {
            money_format: window.globalVariables.money_format,
            template: window.globalVariables.template,
            cart_item_count: window.globalVariables.cart?.item_count,
            cart_total_price: window.globalVariables.cart?.total_price
        };
    }

    // Add current product context if on product page
    if (window.globalVariables?.product) {
        context.product_info = {
            id: window.globalVariables.product.id,
            handle: window.globalVariables.product.handle,
            current_variant_id: window.globalVariables.product.currentVariant?.id
        };
    }

    return context;
};

/**
 * Report error to Rollbar with full context (asynchronous)
 * @param {string} message - Error message
 * @param {object} errorDetails - Error details
 */
const reportErrorToRollbar = async (message, errorDetails) => {
    // Only report if Rollbar is available and this is a critical error
    if (typeof Rollbar === 'undefined') return;
    
    if (shouldIgnoreError(message, errorDetails?.source)) return;

    try {
        // Use setTimeout to ensure non-blocking execution
        setTimeout(() => {
            const context = getErrorContext();
            
            Rollbar.error(message, {
                ...errorDetails,
                custom_context: context,
                fingerprint: `${message}-${errorDetails?.source || 'unknown'}-${errorDetails?.lineno || 0}`
            });
        }, 0);
    } catch (rollbarError) {
        // Avoid infinite loops - log to console only
        console.error('[Rollbar] Failed to report error:', rollbarError);
    }
};

/**
 * Global error handler for uncaught errors
 */
window.onerror = function(message, source, lineno, colno, error) {
    const errorDetails = {
        message: message,
        source: source,
        lineno: lineno,
        colno: colno,
        error_stack: error?.stack,
        error_name: error?.name,
        url: window.location.href,
        timestamp: new Date().toLocaleString()
    };
    
    reportErrorToRollbar(message, errorDetails);
    
    // Return false to allow default error handling
    return false;
};

/**
 * Global unhandled promise rejection handler
 */
window.addEventListener('unhandledrejection', function(event) {
    const message = `Unhandled Promise Rejection: ${event.reason}`;
    const errorDetails = {
        message: message,
        source: 'promise-rejection',
        promise_reason: event.reason,
        url: window.location.href,
        timestamp: new Date().toLocaleString()
    };
    
    reportErrorToRollbar(message, errorDetails);
});

/**
 * Handle ResizeObserver errors specifically (common with Swiper/layout changes)
 */
window.addEventListener('error', function(event) {
    if (event.message && event.message.toLowerCase().includes('resizeobserver')) {
        // Prevent ResizeObserver errors from propagating - they're usually harmless
        event.preventDefault();
        event.stopPropagation();
        return true; // Suppress error
    }
});

/**
 * Initialize error monitoring with proper context
 */
document.addEventListener('DOMContentLoaded', function() {
    // Log successful initialization (for debugging purposes only)
    if (typeof Rollbar !== 'undefined' && window.location.hostname.includes('myshopify.com') === false) {
        console.log('[Rollbar] Error monitoring initialized for production');
    }
});